import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { environment } from 'src/environments/environment';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../../../../ui/button/button.component';
import { SharedModule } from '../../../../shared/shared.module';
import { IProduct } from 'src/app/features/produits/models/products.models';
import { ICurrency } from 'src/app/features/customers/models/customers.models';
import { LocalStorageService } from 'src/app/shared/localstorage.service';

@Component({
  selector: 'app-catalog-product-details',
  standalone: true,
  imports: [CommonModule, ButtonComponent, SharedModule],
  templateUrl: './catalog-product-details.component.html',
  styleUrls: ['./catalog-product-details.component.scss'],
})
export class CatalogProductDetailsComponent implements OnChanges {
  @Input() product: IProduct | null = null;
  @Output() quantityChanged = new EventEmitter<{ productId: number; quantity: number }>();
  @Input() quoteProductsData: any;
  @Output() closed = new EventEmitter<void>();

  baseUrl = environment.BASE_URL_API;
  isDetailsVisible = false;
  selectedProduct: IProduct | null = null;
  isDescriptionExpanded = false;
  shouldShowToggle = false;
  currentImageIndex = 0;
  allImages: string[] = [];
  quantity: number = 0;
  default_currency: ICurrency = LocalStorageService.get('currentUser')?.user?.currency!;

  constructor(private cdRef: ChangeDetectorRef) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['product'] && this.product) {
      this.selectedProduct = { ...this.product };

      this.quantity = this.selectedProduct.quantity ?? 0;

      const mainImage = this.selectedProduct.image ? [this.selectedProduct.image] : [];
      const extraImages = Array.isArray(this.selectedProduct.images) ? this.selectedProduct.images : [];
      this.allImages = [...mainImage, ...extraImages];

      this.isDetailsVisible = true;

      this.currentImageIndex = 0;

      setTimeout(() => {
        const element = document.querySelector('.description-text');
        if (element) {
          this.shouldShowToggle = element.scrollHeight > element.clientHeight;
          this.cdRef.detectChanges();
        }
      }, 200);
    }
  }

  selectImage(index: number): void {
    this.currentImageIndex = index;
  }

  goToProduct(): void {
    const templateId = (this.selectedProduct as any)?.product_template_id || this.selectedProduct?.id;
    if (templateId) {
      const baseHref = document.querySelector('base')?.getAttribute('href') || '/';
      const url = `${window.location.origin}${baseHref}inventory/${templateId}`;
      window.open(url, '_blank');
    }
  }

  increaseQuantity() {
    if (!this.product) return;

    const max = this.product.max_qte ?? Infinity;
    this.product.quantity = Math.min((this.product.quantity ?? 0) + 1, max);
    this.emitQuantityChange();
  }

  decreaseQuantity() {
    if (!this.product) return;

    const min = this.product.min_qte ?? 0;
    this.product.quantity = Math.max((this.product.quantity ?? 0) - 1, min);
    this.emitQuantityChange();
  }

  onQuantityChange(value: number) {
    if (!this.product) return;

    const min = this.product.min_qte ?? 0;
    const max = this.product.max_qte ?? Infinity;
    this.product.quantity = Math.max(Math.min(value, max), min);
    this.emitQuantityChange();
  }

  private emitQuantityChange() {
    this.quantityChanged.emit({
      productId: this.product!.product_id ?? 0,
      quantity: this.product!.quantity ?? 0
    });
  }

  prevImage(): void {
    if (this.allImages.length > 0) {
      this.currentImageIndex =
        (this.currentImageIndex - 1 + this.allImages.length) % this.allImages.length;
    }
  }

  nextImage(): void {
    if (this.allImages.length > 0) {
      this.currentImageIndex = (this.currentImageIndex + 1) % this.allImages.length;
    }
  }

  toggleDescription(): void {
    this.isDescriptionExpanded = !this.isDescriptionExpanded;
  }

  getPriceCurrency(product: IProduct): string {
    const mainCurrency = (product.currencies as unknown as ICurrency[])?.find(c => c.is_main);
    return mainCurrency?.symbol || this.default_currency?.symbol || '€';
  }

  getFinalPrice(product: IProduct): number {
    return product.final_price && product.final_price > 0
      ? product.final_price
      : product.sales_price;
  }

  getMarginAmount(product: IProduct): number {
    const finalPrice = product.final_price && product.final_price > 0
      ? product.final_price
      : product.sales_price;
    return finalPrice - (product.cost ?? 0);
  }

  getMarginPercent(product: IProduct): number {
    const margin = this.getMarginAmount(product);
    return product.cost && product.cost > 0 ? (margin / product.cost) * 100 : 0;
  }

  onImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = 'assets/images/no-img.svg';
  }

  closeDetails(): void {
    this.isDetailsVisible = false;
    this.selectedProduct = null;
    this.closed.emit();
  }
}
